import { useState, useEffect, useRef } from "react";
import {
  Box,
  Typography,
  Button,
  Grid,
  Card,
  CardContent,
  CardActions,
  FormControlLabel,
  LinearProgress,
  Checkbox,
  Toolbar,
} from "@mui/material";
import {
  getStorage,
  ref,
  uploadBytesResumable,
  getDownloadURL,
  deleteObject,
} from "firebase/storage";
import { CloudUpload, Delete, FileDownload } from "@mui/icons-material";

// Components
import ConfirmationModal from "../utils/ConfirmationModal";
import { useNotification } from "../utils/Notification";
import { trackEvent } from "../utils/Utils";

import File from "../modals/File";
import Plans from "../modals/Plans";

// Context
import { useAuth } from "../../contexts/AuthContext";
import { v4 as uuidv4 } from "uuid";
import { useTranslation } from "react-i18next";

// Firebase
import { app } from "../../firebaseConfig";

// Supabase
import supabase from "../../supabaseClient";

export default function PolicyFiles() {
  const { t } = useTranslation();
  const [files, setFiles] = useState([]);
  const [showDeleteConfirmation, setShowDeleteConfirmation] = useState(false);
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [fileModalOpen, setFileModalOpen] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [file, setFile] = useState(null);
  const fileInputRef = useRef(null);
  const [plansModalOpen, setPlansModalOpen] = useState(false);
  const [planData, setPlanData] = useState([]);

  // Context
  const { teamId } = useAuth();
  const { showNotification } = useNotification();

  // Firebase
  const storage = getStorage(app);

  useEffect(() => {
    const fetchFiles = async () => {
      const { data, error } = await supabase
        .from("files")
        .select("*")
        .eq("type", "policyfile");

      if (error) {
        showNotification(
          t("files.errorFetchingFiles", { error: error.message }),
          "error"
        );
      } else {
        setFiles(data);
      }
    };

    const fetchPlan = async () => {
      const { data, error } = await supabase
        .from("teams")
        .select("plan(attachments_limit)")
        .single();

      if (!error) {
        setPlanData(data.plan);
      }
    };

    fetchFiles();
    fetchPlan();
  }, [showDeleteConfirmation, fileModalOpen, plansModalOpen]);

  const handleCloseImportModal = () => {
    setFileModalOpen(false);
    trackEvent("files-import_modal_closed");
  };

  const handleFileSelect = (fileId) => {
    setSelectedFiles((prevSelected) =>
      prevSelected.includes(fileId)
        ? prevSelected.filter((id) => id !== fileId)
        : [...prevSelected, fileId]
    );
    trackEvent("files-file_selected");
  };

  const handleDelete = async () => {
    setShowDeleteConfirmation(true);
    trackEvent("files-delete_clicked");
  };

  const handleBulkDelete = async () => {
    setShowDeleteConfirmation(false);
    try {
      const remainingFiles = files.filter(
        (file) => !selectedFiles.includes(file.id)
      );

      for (const fileId of selectedFiles) {
        const file = files.find((f) => f.id === fileId);
        const storageRef = ref(
          storage,
          `files/${teamId}/${file.id}/${file.path}`
        );
        await deleteObject(storageRef);

        await supabase.from("files").delete().eq("id", file.id);
      }

      // Update state to remove deleted files
      setFiles(remainingFiles);
      setSelectedFiles([]);
      showNotification(t("files.filesDeletedSuccessfully"), "success");
      trackEvent("files-bulk_deleted");
    } catch (error) {
      showNotification(
        t("files.errorDeletingFile", { error: error.message }),
        "error"
      );
    }
  };

  const handleFileDrop = (e) => {
    e.preventDefault();
    const droppedFiles = Array.from(e.dataTransfer.files);
    uploadFiles(droppedFiles);
    trackEvent("files-file_dropped");
  };

  const handleFileChange = (e) => {
    const selectedFiles = Array.from(e.target.files);
    uploadFiles(selectedFiles);
  };

  const uploadFiles = async (filesArray) => {
    const maxFileSize = 25 * 1024 * 1024; // 25MB limit
    if (planData.attachments_limit <= files.length + filesArray.length) {
      showNotification(t("files.uploadLimitReached"), "warning");
      setPlansModalOpen(true);
      return;
    }

    for (const file of filesArray) {
      if (file.size > maxFileSize) {
        showNotification(t("files.fileSizeExceeded"), "warning");
        continue; // Skip large files
      }

      setIsUploading(true);

      try {
        const fileUuid = uuidv4();

        // Upload to Firebase Storage
        const storageRef = ref(
          storage,
          `files/${teamId}/${fileUuid}/${file.name}`
        );
        const metadata = {
          contentDisposition: `attachment; filename="${file.name}"`, // Ensures correct filename when sending through WhatsApp
        };

        const uploadTask = uploadBytesResumable(storageRef, file, metadata);

        uploadTask.on(
          "state_changed",
          (snapshot) => {
            const progress =
              (snapshot.bytesTransferred / snapshot.totalBytes) * 100;
            setUploadProgress(progress);
          },
          (error) => {
            showNotification(
              t("files.errorUploadingFile", { error: error.message }),
              "error"
            );
            setIsUploading(false);
            setUploadProgress(0);
          },
          async () => {
            const downloadURL = await getDownloadURL(uploadTask.snapshot.ref);
            const sanitizedName = file.name.split(".")[0];

            // Update Supabase with the file URL

            const { data, error: supabaseError } = await supabase
              .from("files")
              .insert([
                {
                  id: fileUuid,
                  team_id: teamId,
                  name: sanitizedName,
                  path: file.name,
                  url: downloadURL,
                  type: "policyfile",
                },
              ])
              .single()
              .select();
            if (supabaseError) {
              showNotification(
                t("files.errorSavingFile", { error: supabaseError.message }),
                "error"
              );
            } else {
              setFiles((prevFiles) => [...prevFiles, data]);
            }
            setIsUploading(false);
            setUploadProgress(0);
          }
        );
      } catch (error) {
        setIsUploading(false);
        setUploadProgress(0);
        showNotification(
          t("files.errorUploadingFile", { error: error.message }),
          "error"
        );
      }
    }
    fileInputRef.current.value = ""; // Reset input
  };

  const handleOpenFileModal = (file) => {
    trackEvent("files-file_modal_opened");
    setFile(file);
    setFileModalOpen(true);
  };

  const handleBoxClick = () => {
    trackEvent("files-upload_clicked");
    if (planData.attachments_limit <= files.length) {
      showNotification(t("files.uploadLimitReached"), "warning");
      setPlansModalOpen(true);
      return;
    }
    fileInputRef.current.click();
  };

  const handleClosePlansModal = () => {
    setPlansModalOpen(false);
  };

  const handleExport = () => {
    trackEvent("files-export_clicked");
    // Define CSV headers
    const headers = [
      "id",
      "name",
      "description",
      "path",
      "file_url",
      "created_at",
    ];

    // Get selected files data
    const selectedData = files
      .filter((file) => selectedFiles.includes(file.id))
      .map((file) => [
        file.id,
        file.name,
        file.description || "",
        file.path,
        file.url,
        file.created_at,
      ]);

    // Convert to CSV format
    const csvContent =
      "data:text/csv;charset=utf-8," +
      [headers, ...selectedData].map((row) => row.join(",")).join("\n");

    // Create a download link
    const encodedUri = encodeURI(csvContent);
    const link = document.createElement("a");
    link.href = encodedUri;
    link.download = "files_export.csv";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  return (
    <Box m="20px">
      <Box
        onClick={handleBoxClick}
        onDrop={handleFileDrop}
        onDragOver={(e) => e.preventDefault()}
        sx={{
          border: "2px dashed #ccc",
          p: 4,
          textAlign: "center",
          cursor: "pointer",
          borderRadius: 2,
          backgroundColor: "action.hover",
          "&:hover": {
            backgroundColor: "action.selected",
            borderColor: "primary.main",
          },
        }}
        mt="14px"
        mb="14px"
      >
        {!isUploading && (
          <>
            <CloudUpload sx={{ fontSize: 40, color: "primary.main" }} />
            <Typography variant="h6" mb={2}>
              {t("files.dragDropText")}
            </Typography>
          </>
        )}

        {isUploading && (
          <LinearProgress
            variant="determinate"
            value={uploadProgress}
            sx={{ mt: 2 }}
          />
        )}
      </Box>

      {/* Bulk Actions Toolbar */}
      {selectedFiles.length > 0 && (
        <Toolbar
          sx={{ mb: 2, backgroundColor: "action.hover", borderRadius: 2 }}
        >
          {/* Select All Checkbox */}
          <FormControlLabel
            control={
              <Checkbox
                checked={
                  selectedFiles.length === files.length && files.length > 0
                }
                indeterminate={
                  selectedFiles.length > 0 &&
                  selectedFiles.length < files.length
                }
                onChange={(e) => {
                  if (e.target.checked) {
                    setSelectedFiles(files.map((file) => file.id)); // Select all files
                  } else {
                    setSelectedFiles([]); // Deselect all files
                  }
                }}
              />
            }
          />

          <Typography variant="h6" sx={{ flexGrow: 1 }}>
            {selectedFiles.length} {t("files.selected")}
          </Typography>
          <Button
            variant="contained"
            color="error"
            startIcon={<Delete />}
            onClick={handleDelete}
          >
            {t("files.deleteSelected")}
          </Button>
          <Button
            variant="contained"
            color="primary"
            startIcon={<FileDownload />}
            onClick={handleExport}
            sx={{ ml: 2 }}
            disabled={selectedFiles.length === 0}
          >
            {t("files.exportSelected")}
          </Button>
        </Toolbar>
      )}

      <Grid container spacing={2}>
        {files.map((file) => (
          <Grid item xs={12} sm={6} md={4} key={file.id}>
            <Card
              sx={{
                backgroundColor: selectedFiles.includes(file.id)
                  ? "action.selected"
                  : "secondary",
                border: selectedFiles.includes(file.id)
                  ? "2px solid #1976d2"
                  : "none",
              }}
            >
              <CardContent onClick={() => handleFileSelect(file.id)}>
                <Box display="flex" alignItems="center">
                  <Checkbox
                    checked={selectedFiles.includes(file.id)}
                    onChange={() => handleFileSelect(file.id)}
                    onClick={(e) => e.stopPropagation()}
                  />
                  <Typography variant="h6" component="div" fontWeight="bold">
                    {file.name}
                  </Typography>
                </Box>
                <Typography variant="body2" color="text.secondary">
                  {file.description}
                </Typography>
              </CardContent>

              <CardActions sx={{ display: "flex", gap: 1 }}>
                <Button
                  size="small"
                  href={file.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  color="secondary"
                  variant="contained"
                >
                  {t("files.downloadFile")}
                </Button>

                <Button
                  size="small"
                  color="primary"
                  onClick={() => handleOpenFileModal(file)}
                  variant="contained"
                >
                  {t("files.edit")}
                </Button>
              </CardActions>
            </Card>
          </Grid>
        ))}
      </Grid>

      <ConfirmationModal
        open={showDeleteConfirmation}
        onClose={() => setShowDeleteConfirmation(false)}
        onConfirm={handleBulkDelete}
        title={t("files.confirmDeleteTitle")}
        message={t("files.confirmDeleteMessage")}
      />

      <File open={fileModalOpen} onClose={handleCloseImportModal} file={file} />

      <input
        type="file"
        multiple // Allows multiple file selection
        onChange={handleFileChange}
        ref={fileInputRef}
        hidden
      />

      <Plans open={plansModalOpen} onClose={handleClosePlansModal} />
    </Box>
  );
}
